import { Feather } from "@expo/vector-icons";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type MapFilterDropdownOption = {
  value: string;
  label: string;
  icon?: string;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: MapFilterDropdownOption[];
  placeholder: string;
  disabled?: boolean;
};

type ListboxRect = { top: number; left: number; width: number };

export default function MapFilterDropdown({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<ListboxRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const updateRect = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const isSmallScreen = vw < 640;
    const desiredWidth = isSmallScreen
      ? vw - 2 * margin
      : Math.max(r.width, 240);
    const width = Math.min(desiredWidth, vw - 2 * margin);
    const left = Math.min(
      Math.max(r.left, margin),
      Math.max(margin, vw - width - margin),
    );
    setRect({ top: r.bottom + 4, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScrollOrResize = () => updateRect();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateRect]);

  const listbox =
    open && rect && typeof document !== "undefined"
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            className="fixed bg-white border border-neutral-300 rounded shadow-lg p-1 m-0 list-none overflow-y-auto max-h-72 z-[2147483647]"
            style={{ top: rect.top, left: rect.left, width: rect.width }}
          >
            {options.map((o) => {
              const isSelected = o.value === value;
              return (
                <li
                  key={o.value || "__empty__"}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 px-2.5 py-2 rounded cursor-pointer text-base text-neutral-900 aria-selected:bg-indigo-50 hover:bg-neutral-100"
                >
                  {o.icon ? (
                    <span
                      aria-hidden
                      className="text-base leading-none flex-shrink-0"
                    >
                      {o.icon}
                    </span>
                  ) : null}
                  <span className="flex-1 break-words">{o.label}</span>
                </li>
              );
            })}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full pl-3 pr-9 py-2.5 rounded border border-neutral-300 bg-white text-base text-neutral-900 flex items-center gap-2 text-left relative hover:border-neutral-400 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ fontFamily: "inherit", boxSizing: "border-box" }}
      >
        {selected ? (
          <>
            {selected.icon ? (
              <span aria-hidden className="text-base leading-none">
                {selected.icon}
              </span>
            ) : null}
            <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">
              {selected.label}
            </span>
          </>
        ) : (
          <span className="text-neutral-400 flex-1">{placeholder}</span>
        )}
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none flex">
          <Feather name="chevron-down" size={16} color="#555" />
        </span>
      </button>
      {listbox}
    </div>
  );
}
